import type { UserSummary } from '@nexo/types';
import { cn } from '@nexo/ui';
import { BadgeCheck, BellOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { formatRelative } from '../../lib/format';
import { t } from '../../lib/i18n';

/** The freshest copy of a user: team members are kept live by realtime events. */
export function useLiveUser(user: UserSummary): UserSummary {
  const { memberById } = useTeam();
  return memberById(user.id) ?? user;
}

export function statusLabel(user: UserSummary): string | undefined {
  if (!user.status) return undefined;
  const until = user.status.expiresAt
    ? ` ${t('(hasta {{time}})', { time: formatRelative(user.status.expiresAt) })}`
    : '';
  return `${user.status.emoji} ${user.status.text}${until}`.trim();
}

/** Status emoji + do-not-disturb / away markers shown next to a name. */
export function UserBadges({ user, className }: { user: UserSummary; className?: string }) {
  const { t } = useTranslation();
  if (!user.status && !user.dnd && !user.away && !user.staff) return null;
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1 align-middle', className)}>
      {user.staff && <StaffBadge />}
      {user.status && (
        <span title={statusLabel(user)} className="text-[13px] leading-none">
          {user.status.emoji}
        </span>
      )}
      {user.dnd && (
        <span title={t('No molestar')} aria-label={t('No molestar')} className="text-danger">
          <BellOff className="size-3" />
        </span>
      )}
      {user.away && !user.dnd && (
        <span className="text-[10px] font-medium text-subtle" title={t('Ausente')}>
          {t('ausente')}
        </span>
      )}
    </span>
  );
}

/** Marks people from the Nexo team (platform admins), wherever their name appears. */
export function StaffBadge({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      title={t('Equipo de Nexo')}
      aria-label={t('Equipo de Nexo')}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-md bg-[linear-gradient(100deg,oklch(0.62_0.19_262),oklch(0.72_0.16_55))] px-1 py-px text-[9.5px] leading-none font-bold tracking-wide text-white',
        className,
      )}
    >
      <BadgeCheck className="size-2.5" />
      NEXO
    </span>
  );
}
